import { Injectable, OnDestroy } from '@angular/core'
import { environment } from '../../../../../environments/environment'
import { CookieKeys } from '../../enums/cookie-keys'
import { MbDocumentService } from '../mb-document-service/mb-document.service'
import { CookieService } from 'ngx-cookie-service'
import { WindowRef } from '@spartacus/core'
import { BehaviorSubject, Subscription } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { UcService } from '../../enums/uc-service'
import { UcManagementService } from '../usercentrics-consent-manager/uc-management.service'
import { MbModalService } from '../../../shared/services/modal.service'
import { FreshChatBot, FreshChatWebWidget } from '../../components/types'

@Injectable({ providedIn: 'root' })
export class ChatBotService implements OnDestroy {

  constructor (private mbDocument: MbDocumentService,
               private cookieService: CookieService,
               private windowRef: WindowRef,
               private ucManagement: UcManagementService,
               private modalService: MbModalService) {
    this.subscription.add(this.ucManagement.isServiceBlocked$(UcService.FRESH_CHAT)
      .pipe(filter(isBlocked => isBlocked))
      .subscribe(() => {
        this.deActivateBot()
        this.deActivateWidget()
      }))
  }

  addWidgetScript (uuid: string, host: string, token: string) {
    if (!this.mbDocument.getElementById(this.widgetScriptId) && !this.widgetInitInProgress$.value) {
      this.widgetInitInProgress$.next(true)
      const wrapper = this.mbDocument.getElementsByTagName('script')?.item(0) as HTMLElement
      const script = this.mbDocument.createScriptElement()
      let loaded = false
      script.id = this.widgetScriptId
      script.async = true
      script.src = environment.freshChat.widget.src
      wrapper?.parentNode?.insertBefore(script, wrapper)
      script.onload = () => {
        if (!loaded) {
          this.initWidget(uuid, host, token)
        }
        loaded = true
        this.widgetInitInProgress$.next(false)
      }
    }
  }

  addBotScript (clientId: string, hash: string, open: boolean) {
    if (!this.mbDocument.getElementById(this.botScriptId) && !this.botInitInProgress$.value) {
      this.botInitInProgress$.next(true)
      const wrapper = this.mbDocument.getElementsByTagName('script')?.item(0) as HTMLElement
      const script = this.mbDocument.createScriptElement()
      let loaded = false
      script.id = this.botScriptId
      script.async = true
      script.src = environment.freshChat.bot.src
      script.setAttribute('data-client', clientId)
      script.setAttribute('data-self-init', 'false')
      script.setAttribute('data-init-type', 'opt')
      script.setAttribute('data-bot-hash', hash)
      script.setAttribute('data-env', 'prod')
      script.setAttribute('data-region', 'euc')
      script.onload = () => {
        if (!loaded) {
          this.initBot(open)
        }
        loaded = true
        this.botInitInProgress$.next(false)
      }
      wrapper?.parentNode?.insertBefore(script, wrapper)
    } else if (this.mbDocument.getElementById(this.botScriptId) && !this.mbDocument.getElementById(this.botElementId)) {
      this.subscription.add(this.botInitInProgress$
        .pipe(filter(value => !value))
        .pipe(take(1))
        .subscribe(() => this.activateBot()))
    }
  }

  initBot (open?: boolean) {
    const window = this.windowRef?.document?.defaultView as any
    if (!window || !window.Freshbots) {
      return
    }
    this.botRef$.next(window.Freshbots)
    window.Freshbots.initiateWidget(
      {
        autoInitChat: false,
        getClientParams: () => ({ 'cstmr::xtrInfrmtn:Location': this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY) })
      },
      () => {
        if (open || this.showWidgetWithNextBot) {
          this.openBot()
          this.showWidgetWithNextBot = false
        }
      }
    )
  }

  initWidget (widgetUuid: string, host: string, token: string) {
    const window = this.windowRef?.document?.defaultView as any
    const widget: FreshChatWebWidget = window.fcWidget
    if (!window || !widget) {
      return
    }
    this.widgetRef$.next(widget)
    widget.init({ token, host, widgetUuid })
    const miltenyiCountryOrigin = this.cookieService.get(CookieKeys.MILTENYI_COUNTRY_ORIGIN)
    const miltenyiSelectedCountry = this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY)
    widget.user.setProperties({
      /* eslint-disable @typescript-eslint/naming-convention */
      cf_ip_country: miltenyiCountryOrigin,
      cf_url_country: miltenyiSelectedCountry
      /* eslint-enable @typescript-eslint/naming-convention */
    })
  }

  openBot () {
    if (this.ucManagement.isServiceBlocked(UcService.FRESH_CHAT)) {
      this.modalService.openConsentModal(this.botScriptId, UcService.FRESH_CHAT)
    } else {
      this.botRef$.value?.showWidget(true)
    }
  }

  activateBot () {
    this.subscription.add(this.botRef$
      .pipe(filter(value => this.windowRef.isBrowser() && !!this.windowRef.document?.defaultView && !!value))
      .pipe(take(1))
      .pipe(filter(() => !this.mbDocument.getElementById(this.botElementId)))
      .pipe(map(freshBot => freshBot as FreshChatBot))
      .subscribe(freshBot => freshBot.activate()))
  }

  deActivateBot () {
    this.subscription.add(this.botRef$
      .pipe(filter(value => !!value || this.ucManagement.isServiceBlocked(UcService.FRESH_CHAT)))
      .pipe(take(1))
      .subscribe(freshBot => (freshBot as FreshChatBot)?.deActivate()))
  }

  deActivateWidget () {
    this.subscription.add(this.widgetRef$
      .pipe(filter(value => !!value))
      .pipe(take(1))
      .subscribe(widget => (widget as FreshChatWebWidget)?.destroy()))
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  setShowWidgetWithNextBotAction (showWidgetWithNextBot: boolean) {
    this.showWidgetWithNextBot = showWidgetWithNextBot
  }

  private showWidgetWithNextBot = false
  private subscription = new Subscription()
  private botRef$ = new BehaviorSubject<FreshChatBot | undefined>(undefined)
  private widgetRef$ = new BehaviorSubject<FreshChatWebWidget | undefined>(undefined)
  private botInitInProgress$ = new BehaviorSubject<boolean>(false)
  private widgetInitInProgress$ = new BehaviorSubject<boolean>(false)

  private readonly botElementId = 'fbots-wdgt'
  private readonly botScriptId = 'spd-busns-spt'
  private readonly widgetScriptId = 'Freshchat-js-sdk'
}
